import type { EntraIdComponents } from "@p0security/integrations-entra-id-shared";
import type { FrontendInstaller } from "@p0security/integrations-installer";
import { Typography } from "@p0security/ui";

export const legacyEntraIdInstaller: FrontendInstaller<
  typeof EntraIdComponents
> = {
  components: {
    base: { items: {} },
    "iam-write": {
      labeler: () => "Enter your Entra ID tenant ID",
      instructions: (context, id) => {
        const serviceAccountId = context.config.base[id]?.serviceAccountId;
        const serviceAccountEmail =
          context.config.base[id]?.serviceAccountEmail;
        return {
          help: (
            <div>
              <Typography.Paragraph>
                To use P0 with Entra ID, start by creating a new App
                Registration.
              </Typography.Paragraph>
            </div>
          ),
          commands: {
            console: (
              <div>
                <Typography.Paragraph>
                  To install manually, follow these steps:
                </Typography.Paragraph>
                <Typography.Paragraph>
                  1. Create a new App Registration in your Entra ID tenant
                </Typography.Paragraph>
                <ul>
                  <li>
                    Select{" "}
                    <Typography.Text code>
                      Accounts in any organizational directory (Any Microsoft
                      Entra ID tenant - Multitenant)
                    </Typography.Text>
                  </li>
                  <li>
                    Add{" "}
                    <Typography.Text code>
                      https://p0.app/integration/auth/_redirect
                    </Typography.Text>{" "}
                    as a web redirect URI
                  </li>
                </ul>
                <Typography.Paragraph>
                  2. Configure the following Microsoft Graph API permissions
                  under <Typography.Text code>API permissions</Typography.Text>{" "}
                  as &quot;Application permissions&quot;:
                </Typography.Paragraph>
                <ul>
                  <li>
                    <Typography.Text code>User.Read.All</Typography.Text> - Read
                    all users
                  </li>
                  <li>
                    <Typography.Text code>Group.Read.All</Typography.Text> -
                    Read all groups
                  </li>
                  <li>
                    <Typography.Text code>GroupMember.Read.All</Typography.Text>{" "}
                    - Read group members
                  </li>
                  <li>
                    <Typography.Text code>
                      GroupMember.ReadWrite.All
                    </Typography.Text>{" "}
                    - Add and remove members in all groups
                  </li>
                  <li>
                    <Typography.Text code>
                      RoleManagement.ReadWrite.Directory
                    </Typography.Text>{" "}
                    - Read and manage directory role assignments
                  </li>
                </ul>
                <Typography.Paragraph>
                  3. Grant admin consent
                </Typography.Paragraph>
                <Typography.Paragraph>
                  4. Create a federated credential for the P0 Security service
                  account. Navigate to &quot;Certificates and Secrets&quot;,
                  &quot;Federated Credentials&quot;, then click &quot;Add
                  credential&quot;.
                </Typography.Paragraph>
                <ul>
                  <li>
                    Select &quot;Other issuer&quot; and set{" "}
                    <Typography.Text code>
                      https://accounts.google.com
                    </Typography.Text>
                    as the issuer.
                  </li>
                  <li>
                    Set &quot;Type&quot; to{" "}
                    <Typography.Text code>
                      explicit subject identifier
                    </Typography.Text>{" "}
                    (the default)
                  </li>
                  <li>
                    Set &quot;Value&quot; to{" "}
                    <Typography.Text code>{serviceAccountId}</Typography.Text>
                  </li>
                  <li>
                    Set &quot;Name&quot; to{" "}
                    <Typography.Text code>
                      {serviceAccountEmail}
                    </Typography.Text>
                  </li>
                </ul>
                <Typography.Paragraph>
                  5. Take note of the &quot;Application (client) ID&quot; value.
                  You will need it to complete the installation.
                </Typography.Paragraph>
              </div>
            ),
          },
        };
      },
      items: {
        clientId: {
          labeler: () => "Enter your Entra ID application client ID (required)",
        },
        sovereignCloudId: {
          options: {},
          labeler: () =>
            "If you are on portal.azure.com, select AzureCloud. If you are on portal.azure.us, select AzureUSGovernment.",
        },
      },
    },
  },
};
